import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';

import { FootnoteHintsService } from 'src/app/core/services/footnote-hints/footnote-hints.service';
import { MarketConfigService } from 'src/app/core/services/market-config/market-config.service';
import { Vehicle } from 'src/app/core/vehicle/vehicle.interface';
import { ObjectStorage } from 'src/app/utils/object-storage/object-storage';

import {
  VehicleDataCollectionConfig,
  VehicleDataCollectionConfigData
} from './vehicle-data-collection-engine-config.interface';
import { VehicleDataCollectionItem } from './vehicle-data-collection-item';

/**
 * Given a vehicle and the respective collection config key, this service
 * creates collections of vehicle data items with respective label, values,
 * icons and footnotes.
 */
@Injectable({
  providedIn: 'root'
})
export class VehicleDataCollectionService {
  private readonly collectionCache = new Map<string, Observable<VehicleDataCollectionItem[]>>();

  public constructor(
    private readonly marketConfigService: MarketConfigService,
    private readonly footnoteHintsService: FootnoteHintsService
  ) {}

  /**
   * Create a Vehicle Data Collection from collectionConfigPath and vehicle
   */
  public getCollection(
    collectionConfigPath: string,
    vehicle: Vehicle | ObjectStorage<Vehicle>,
    footnoteContext?: string
  ): Observable<VehicleDataCollectionItem[]> {
    const vehicleObj = vehicle instanceof ObjectStorage ? vehicle : new ObjectStorage<Vehicle>(vehicle);

    const collectionKey = `${vehicleObj.get('vehicleId')}|${collectionConfigPath}`;

    if (this.collectionCache.has(collectionKey)) {
      return this.collectionCache.get(collectionKey);
    }

    const collection$ = this.marketConfigService.observe<VehicleDataCollectionConfig>(collectionConfigPath).pipe(
      // create a vehicle data collection from collection config
      map(engineConceptConfigs => {
        // get collection config for given vehicle's engine concept
        const engineConceptConfig = this.getEngineConceptConfig(engineConceptConfigs, vehicleObj);
        const collectionConfig = this.buildCompleteConfig(engineConceptConfig);

        // create an empty collection
        const collection = new Map<string, VehicleDataCollectionItem>();

        // iterate through config items
        (collectionConfig || []).forEach(configItem => {
          // create a collection item from vehicle and config item
          const item = new VehicleDataCollectionItem(vehicleObj, configItem || []);

          // if the item is valid (has values), add it to the collection
          if (item.isValid) {
            collection.set(item.key, item);
          }
        });

        // return collection as array
        return Array.from(collection.values());
      }),

      // get footnote hints for collection and add them to items
      mergeMap(collection =>
        combineLatest(
          collection.map(item =>
            this.footnoteHintsService
              .getFootnoteHintsForVehicleProperties(vehicleObj, footnoteContext, ...item.propertyPaths)
              .pipe(first())
          )
        ).pipe(
          map(hints =>
            collection.map((item, i) => {
              item.footnotes = hints[i];
              return item;
            })
          )
        )
      )
    );

    this.collectionCache.set(collectionKey, collection$);

    return collection$;
  }

  private buildCompleteConfig(engineConfig: VehicleDataCollectionConfigData = {}): string[][] {
    return [...(engineConfig?.static || []), ...(engineConfig?.wltpAndNedc || []), ...(engineConfig?.standard || [])];
  }

  /**
   * get config partial for given vehicle's engine concept
   */
  private getEngineConceptConfig(configs, vehicleObj: ObjectStorage<Vehicle>): VehicleDataCollectionConfigData {
    // get given vehicle's engine concept or use 'DEFAULT'
    const engineConcept = vehicleObj.get<string>('technicalData.engine.engineConcept.id', 'DEFAULT');
    // if there's no collection for the engine concept, fallback to
    // default collection config or an empty collection
    return configs && configs.length && configs.find(item => item.id === engineConcept)
      ? configs.find(item => item.id === engineConcept)
      : (configs && configs.find(item => item.id === 'DEFAULT')) || {};
  }
}
